import React, { useCallback, useState, useEffect } from 'react'
import { useWatch, useFormContext } from 'react-hook-form'

import {
  DiningOptionBehavior,
  FulfillmentType
} from 'src/apollo/onlineOrdering'
import { t } from 'src/translations'
import { useFulfillment } from 'src/public/components/online_ordering/FulfillmentContext'
import InputField from 'src/shared/components/common/form_input/InputField'
import { formatAddress } from 'src/shared/components/common/form_input/LocationInput'

import PlacesAutocomplete from 'shared/components/common/location_search/PlacesAutocomplete'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import ErrorNotice from 'shared/components/common/error_notice'
import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'
import { LoadingSpinner } from 'shared/components/common/loading_spinner/LoadingSpinner'

import { normalizeGoogleAddress } from 'public/components/online_ordering/addressUtils'

import { gPlacesAPIKey } from 'config'

import { AddressType } from '../../../checkout/shippingUtils'
import { useDeleteShippingRate } from './hooks/useDeleteShippingRate'
import DeliveryFulfillmentTime from './DeliveryFulfillmentTime'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'

// Add type declaration for google maps
declare global {
  interface Window {
    google: typeof google
  }
}

const DeliverySection = () => {
  const firstName = useWatch({ name: 'yourInfoFirstName' })
  const lastName = useWatch({ name: 'yourInfoLastName' })
  const { selectedLocation } = useRestaurant()
  const { isAddressInRange } = useDelivery()
  const [address, setAddress] = useState<AddressType | undefined>()
  const { updateCartFulfillment } = useFulfillment()
  const { cart } = useCart()
  const { register, unregister, setValue } = useFormContext()
  const [loading, setLoading] = useState(false)

  useDeleteShippingRate()

  register('address', {
    required: t('delivery-section.enter-delivery-address-validation'),
    validate: () => {
      return !Boolean(validationError)
    }
  })

  useEffect(() => {
    return () => {
      unregister('address')
    }
  }, [unregister])

  useEffect(() => {
    if (address && !loading) {
      const { name, ...deliveryInfo } = address
      setValue('address', address)
      updateCartFulfillment({
        deliveryInfo,
        // Only set fulfillment type and datetime if not already set in cart
        fulfillmentType: cart?.fulfillmentType || FulfillmentType.Asap,
        fulfillmentDateTime: cart?.fulfillmentDateTime,
        diningOptionBehavior: DiningOptionBehavior.Delivery
      })
    }
  }, [
    address,
    loading,
    updateCartFulfillment,
    cart?.fulfillmentType,
    cart?.fulfillmentDateTime
  ])

  /**
   * validation error state only pertinent to individual screen render lifecycles.
   * this validation error reflects issues upstream from delivery distance validation
   * of an address and restaurant couple (i.e. google auto complete failures)
   */
  const [validationError, setValidationError] = useState<string>('')

  const handleError = () =>
    setValidationError(t('address-search-screen.trouble-verifying-address'))

  const handlePlaceSelected = useCallback(
    async (place: google.maps.places.PlaceResult) => {
      const addressHasStreetNumber = place.address_components?.some(
        (component) => component.types.includes('street_number')
      )
      if (!addressHasStreetNumber) {
        return setValidationError(
          t('address-search-screen.enter-valid-address-with-street-number')
        )
      }

      const normalizedAddress = normalizeGoogleAddress(place)
      if (
        selectedLocation?.externalId &&
        addressHasStreetNumber &&
        normalizedAddress
      ) {
        try {
          setLoading(true)
          const inRange = await isAddressInRange(normalizedAddress)
          setLoading(false)
          if (inRange) {
            setAddress({
              name: `${firstName} ${lastName}`,
              ...normalizedAddress
            })
          } else {
            setValidationError(t('delivery-section.outside-delivery-range'))
          }
        } catch (_) {
          handleError()
          setLoading(false)
        }
      }
    },
    [firstName, lastName, selectedLocation?.externalId, isAddressInRange]
  )

  const onAddress2Change = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (e.target.value && address) {
        const updatedAddress = {
          ...address,
          address2: e.target.value
        }
        setAddress(updatedAddress)
      }
    },
    [address]
  )

  return (
    <div className='deliverySection'>
      <div className='inputLabel'>{t('delivery-section.address-label')}</div>
      <div className='addressInput' data-testid='delivery-address-input'>
        <PlacesAutocomplete
          formInputField
          fieldLabel={t('delivery-section.enter-delivery-address')}
          inputAriaLabel={t('delivery-section.enter-delivery-address')}
          className='addressInputField'
          id='delivery-address-input-autocomplete'
          placeholder={t('delivery-section.enter-delivery-address-validation')}
          defaultValue={
            address
              ? formatAddress({
                  address1: address.address1,
                  city: address.city,
                  state: address.state,
                  zipcode: address.zipCode
                })
              : undefined
          }
          apiKey={gPlacesAPIKey}
          onPlaceSelected={handlePlaceSelected}
          locationBias={
            selectedLocation?.lat && selectedLocation?.long
              ? { lat: selectedLocation.lat, long: selectedLocation.long }
              : undefined
          }
          onError={handleError}
          requireZip
          normalized
        />
        {loading && (
          <div className='validatingOverlay'>
            <LoadingSpinner size='16px' />
          </div>
        )}
      </div>
      <AnimatedSection
        className='addressInputError'
        expanded={Boolean(validationError)}
      >
        <div className='error'>
          <ErrorNotice>{validationError}</ErrorNotice>
        </div>
      </AnimatedSection>
      {address && (
        <InputField
          label={t('delivery-section.apartment-suite-optional')}
          id='address2'
          type='text'
          defaultValue={address?.address2 ? address.address2 : undefined}
          preventFormSubmit
          onBlur={onAddress2Change}
        />
      )}
      {address && <DeliveryFulfillmentTime />}
    </div>
  )
}

export default DeliverySection
